import React from 'react';

import { FormattedDate, FormattedMessage, useIntl } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { type SearchResultWorkItemPartial } from '@atlassian/search-client';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../../../../../controllers/store/search';

import { messages } from './messages';

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

interface WorkItemSearchResultProps {
	partial: SearchResultWorkItemPartial;
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
	isAllProductSelected?: boolean;
}

const getStatusLozengeAppearance = (label: string) => {
	label = label.replace(/[\s]+/g, '').toLowerCase();
	switch (true) {
		case ['onhold'].includes(label):
			return 'default'; // gray

		case ['solved', 'complete'].includes(label):
			return 'success'; // green

		case ['open', 'offtrack'].includes(label):
			return 'removed'; // red

		case ['new', 'atrisk'].includes(label):
			return 'moved'; // yellow

		case ['pending', 'ontrack'].includes(label):
			return 'inprogress'; // blue
	}
	return 'inprogress'; // blue for custom status labels
};

export const WorkItemSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isAllProductSelected,
}: WorkItemSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title } = partial;
	const { assignee, createdBy, dueDate, workItemProject, status, subtype } = entity || {};

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getStatusAndDate = () => {
		if (!status) {
			return null;
		}
		const statusLabel = status.replace(/[-_]+/g, ' ');

		const statusLozenge = (
			<Lozenge appearance={getStatusLozengeAppearance(statusLabel)}>{statusLabel}</Lozenge>
		);
		const dateLozenge = dueDate && (
			<Lozenge>
				<FormattedDate value={dueDate} month="short" day="numeric" />
			</Lozenge>
		);

		return (
			<Box xcss={statusAndDateContainerStyles}>
				{dueDate ? (
					<FormattedMessage {...messages.for} values={{ statusLozenge, dateLozenge }} />
				) : (
					statusLozenge
				)}
			</Box>
		);
	};

	const getAssigneeOrCreator = () => {
		let actionLabel: string;
		const fullName = assignee?.thirdPartyUser?.name || createdBy?.thirdPartyUser?.name || '';
		const firstName = fullName?.split(' ')?.[0];
		if (assignee?.thirdPartyUser?.name) {
			actionLabel = formatMessage(messages.isAssigned, {
				firstName: firstName,
				isAssigned: firstName ? 'internal' : 'external',
			});
		} else {
			actionLabel = formatMessage(messages.created, {
				firstName: firstName,
				created: firstName ? 'internal' : 'external',
			});
		}

		return (
			<Inline space="space.025" alignBlock="center">
				<ProfileAvatar name={fullName} />
				<Box xcss={nameContainer}>{actionLabel}</Box>
			</Inline>
		);
	};

	const subtypeMap: Record<string, any> = {
		task: messages.taskLabel,
		default_task: messages.taskLabel,
		epic: messages.epicLabel,
		bug: messages.bugLabel,
		story: messages.storyLabel,
		milestone: messages.milestoneLabel,
		section: messages.sectionLabel,
		approval: messages.approvalLabel,
		problem: messages.problemLabel,
		incident: messages.incidentLabel,
		question: messages.questionLabel,
		work_item: messages.workItemLabel,
		issue: messages.issueLabel,
		other: messages.workItemLabel,
	};

	const label = formatMessage(
		subtypeMap[subtype?.toLowerCase() ?? 'other'] || messages.workItemLabel,
	);

	const headerMetadataParts = [
		label,
		['project', workItemProject?.name],
		['last-modified', lastModifiedComponent],
		['padlock', padlockIcon],
		['status', getStatusAndDate()],
		['person', getAssigneeOrCreator()],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
			footer={
				<MentionedByLinkedResults
					linkedResults={partial.linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={partial}
					position={position}
					url={url}
					onResultClicked={onResultClicked || (() => { })}
					onResultContextMenu={onResultCtxMenuClicked}
					isAllProductSelected={isAllProductSelected}
				/>
			}
		/>
	);
};
